import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Spinner } from 'react-bootstrap';
import { Button, DataTable, FormField, Pill, ProgressBar, RecordDrawer, Select, controlClass } from '../../../components/EntityList';
import Paginator from '../../Paginator/Paginator';
import { useLabels } from '../../../hooks/useLabels';
import { pickKey } from './autoMap';
import { DatePartsInput, RecordPicker, UnresolvedValues, ValueInput, useReferenceOptions } from './ImportFill';

const PAGE_SIZE = 50;

const STATUS_TONE = { new: 'ok', incomplete: 'warn', existing: 'accent', unchanged: 'neutral', duplicate: 'warn', invalid: 'danger' };
const GROUPS = ['all', 'new', 'incomplete', 'existing', 'unchanged', 'duplicate', 'invalid'];
const SELECTABLE = new Set(['new', 'existing', 'duplicate']);
// Default decision per group: invalid rows can't be imported, unchanged rows have nothing to apply,
// possible duplicates need an explicit opt-in. Incomplete rows are completed and re-validated first.
const INCLUDED_BY_DEFAULT = new Set(['new', 'existing']);

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
	const name = new Intl.DateTimeFormat('es', { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, i, 1)));
	return { value: String(i + 1), label: name.charAt(0).toUpperCase() + name.slice(1) };
});

const asText = (value) => (value === null || value === undefined ? '' : String(value));

export default function StepReview({
	schema,
	validation,
	table,
	mapping,
	progress,
	onApply,
	fills = {},
	onFill,
	picks = {},
	onPick,
	dirty = false,
	validating = false,
	onRevalidate,
}) {
	const { t } = useLabels();
	const [filter, setFilter] = useState('all');
	const [page, setPage] = useState(1);
	const [included, setIncluded] = useState(
		() => new Set(validation.rows.filter((r) => INCLUDED_BY_DEFAULT.has(r.status)).map((r) => r.rowNumber)),
	);
	// Per existing row and field: 'file' (apply the file value) or 'keep'; plus the editable final value
	const [modes, setModes] = useState({});
	const [finals, setFinals] = useState({});
	const [drawerRow, setDrawerRow] = useState(null);

	const cellsByRow = useMemo(() => new Map(table.rows.map((row) => [row.rowNumber, row.cells])), [table]);
	const fieldsByKey = useMemo(() => new Map(schema.fields.map((f) => [f.key, f])), [schema]);
	const summaryFields = useMemo(() => schema.fields.filter((f) => mapping[f.key]).slice(0, 4), [schema, mapping]);
	const fieldLabel = (key) => t(fieldsByKey.get(key)?.labelKey || key);

	// Distinct file values that matched no record, per reference field, with the rows using them
	const unresolvedGroups = useMemo(() => {
		const referenceKeys = new Set(schema.references.map((r) => r.key));
		const groups = new Map();
		validation.rows.forEach((row) =>
			(row.needs || []).forEach((need) => {
				if (!referenceKeys.has(need.field) || need.value === undefined || need.value === null) return;
				const key = pickKey(need.value);
				const id = `${need.field}|${key}`;
				if (!groups.has(id)) groups.set(id, { field: need.field, key, value: need.value, rowNumbers: [], suggestions: need.suggestions || [] });
				groups.get(id).rowNumbers.push(row.rowNumber);
			}),
		);
		return [...groups.values()];
	}, [validation, schema]);

	const neededFields = useMemo(() => {
		const keys = new Set();
		validation.rows.forEach((row) => (row.needs || []).forEach((need) => keys.add(need.field)));
		return schema.fields.filter((f) => keys.has(f.key));
	}, [validation, schema]);
	// Day + month + year picked once per missing date field, copied into every row that needs it
	const [monthYear, setMonthYear] = useState({});
	const dateFields = neededFields.filter((f) => f.type === 'date');
	// Day written in the file for a date built from another column ("Día de vto" for Vencimiento)
	const fileDay = (rowNumber, fieldKey) => {
		const from = fieldsByKey.get(fieldKey)?.fallbackFrom;
		const value = from && mapping[from] ? cellsByRow.get(rowNumber)?.[mapping[from]] : '';
		return value === '' || value === null || value === undefined ? '' : String(value);
	};
	const partsOf = (rowNumber, fieldKey) =>
		fills[rowNumber]?.parts?.[fieldKey] || { day: fileDay(rowNumber, fieldKey), month: '', year: '' };
	const applyMonthYear = (fieldKey, patch) => {
		const next = { ...monthYear[fieldKey], ...patch };
		setMonthYear((prev) => ({ ...prev, [fieldKey]: next }));
		validation.rows
			.filter((row) => (row.needs || []).some((need) => need.field === fieldKey))
			.forEach((row) => {
				const current = partsOf(row.rowNumber, fieldKey);
				const parts = { ...current, day: current.day || fileDay(row.rowNumber, fieldKey) };
				// The day written in the file ("Día de vto") wins over the common day
				if (next.day && !fileDay(row.rowNumber, fieldKey)) parts.day = next.day;
				if (next.month) parts.month = next.month;
				if (next.year) parts.year = next.year;
				onFill([row.rowNumber], { parts: { [fieldKey]: parts } });
			});
	};
	// What a row shows as inputs: what it still needs, plus what the user already completed in it,
	// so a value stays visible (and editable) after re-validating
	const editableItems = (row) => {
		const needs = row.needs || [];
		const fill = fills[row.rowNumber] || {};
		const pending = new Set(needs.map((need) => need.field));
		const done = [...new Set([...Object.keys(fill.values || {}), ...Object.keys(fill.ids || {}), ...Object.keys(fill.parts || {})])]
			.filter((key) => !pending.has(key) && fieldsByKey.has(key))
			.map((key) => ({ field: key, done: true }));
		return [...needs, ...done];
	};
	// Typeahead options for references still missing or already picked inline
	const optionsByField = useReferenceOptions([
		...neededFields.filter((f) => f.type === 'reference').map((f) => f.key),
		...Object.values(fills).flatMap((fill) => Object.keys(fill.ids || {})),
	]);

	const visibleRows = filter === 'all' ? validation.rows : validation.rows.filter((r) => r.status === filter);
	const pageRows = visibleRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	const modeOf = (row, field) => modes[row.rowNumber]?.[field] || 'file';
	const finalOf = (row, diff) => finals[row.rowNumber]?.[diff.field] ?? asText(diff.incoming);
	const setMode = (row, field, mode) =>
		setModes((prev) => ({ ...prev, [row.rowNumber]: { ...prev[row.rowNumber], [field]: mode } }));
	const setAllModes = (row, mode) =>
		setModes((prev) => ({
			...prev,
			[row.rowNumber]: Object.fromEntries((row.diffs || []).map((d) => [d.field, mode])),
		}));
	const setFinal = (row, field, value) =>
		setFinals((prev) => ({ ...prev, [row.rowNumber]: { ...prev[row.rowNumber], [field]: value } }));

	const hasInvisible = (row) =>
		Object.values(mapping).some((header) => table.invisible.has(`${row.rowNumber}|${header}`));

	// Changes actually applied to an existing account: mode 'file', non-empty, different from current
	const changesFor = (row) => {
		const changes = {};
		const expected = {};
		(row.diffs || []).forEach((diff) => {
			if (modeOf(row, diff.field) !== 'file') return;
			const value = finalOf(row, diff).trim();
			if (value === '' || value === asText(diff.current)) return;
			changes[diff.field] = value;
			expected[diff.field] = diff.current;
		});
		return { changes, expected };
	};

	const plan = useMemo(() => {
		const create = [];
		const updates = [];
		validation.rows.forEach((row) => {
			if (!included.has(row.rowNumber)) return;
			if (row.status === 'new' || row.status === 'duplicate') {
				create.push({ rowNumber: row.rowNumber, allowDuplicate: row.status === 'duplicate' });
			} else if (row.status === 'existing') {
				const { changes, expected } = changesFor(row);
				if (Object.keys(changes).length > 0) updates.push({ rowNumber: row.rowNumber, id: row.matchedId, changes, expected });
			}
		});
		return { create, updates };
	}, [validation, included, modes, finals]);

	const toggle = (rowNumber) =>
		setIncluded((prev) => {
			const next = new Set(prev);
			if (next.has(rowNumber)) next.delete(rowNumber);
			else next.add(rowNumber);
			return next;
		});

	const toggleAllVisible = () => {
		const selectable = visibleRows.filter((r) => SELECTABLE.has(r.status)).map((r) => r.rowNumber);
		const allOn = selectable.length > 0 && selectable.every((n) => included.has(n));
		setIncluded((prev) => {
			const next = new Set(prev);
			selectable.forEach((n) => (allOn ? next.delete(n) : next.add(n)));
			return next;
		});
	};

	const previewText = (row) => {
		const p = row.preview || {};
		const parts = [];
		if (p.asset) parts.push(`${t('entity.asset')}: ${p.asset}`);
		if (p.account) parts.push(`${t('entity.account')}: ${p.account}`);
		if (p.due) parts.push(`${t('entity.due')}: ${p.due}`);
		if (row.diffs?.length) parts.push(`${row.diffs.length} cambio(s)`);
		return parts.join(' · ');
	};

	const columns = [
		{ key: 'row', header: t('import.row'), className: 'tw:font-mono tw:w-[64px]', render: (row) => row.rowNumber },
		{
			key: 'status',
			header: t('import.status'),
			render: (row) => (
				<Pill tone={STATUS_TONE[row.status]} dot>
					{t(`import.group.${row.status}`)}
				</Pill>
			),
		},
		{
			key: 'data',
			header: 'Datos',
			render: (row) => {
				const cells = cellsByRow.get(row.rowNumber) || {};
				return (
					<div className='tw:min-w-[220px]'>
						<div className='tw:text-[13px] tw:text-ink-0'>
							{summaryFields.map((f) => asText(cells[mapping[f.key]])).filter(Boolean).join(' · ')}
						</div>
						{previewText(row) && <div className='tw:text-[12px] tw:text-ink-3'>→ {previewText(row)}</div>}
					</div>
				);
			},
		},
		{
			key: 'issues',
			header: t('import.issues'),
			render: (row) => (
				<div className='tw:space-y-0.5 tw:text-[12.5px]'>
					{row.errors.map((error) => (
						<div key={error} className='tw:text-danger'>
							{error}
						</div>
					))}
					{editableItems(row).map((need) => {
						const fill = fills[row.rowNumber];
						const field = fieldsByKey.get(need.field);
						// Missing values are completed right here: plain values with an input, records with a
						// typeahead. File values that matched no record are picked once in "Valores sin identificar".
						const inPanel = field?.type === 'reference' && need.value !== undefined && need.value !== null;
						if (field && !inPanel && !need.auto) {
							return (
								<div
									key={need.field}
									className='tw:flex tw:min-w-[240px] tw:items-center tw:gap-2'
									onClick={(e) => e.stopPropagation()}
									onKeyDown={(e) => e.stopPropagation()}
								>
									<span
										className={classnames(
											'tw:shrink-0',
											need.done ? 'tw:text-ink-1' : need.optional ? 'tw:text-ink-2' : 'tw:text-warn',
										)}
										title={need.reason}
									>
										{fieldLabel(need.field)}:
									</span>
									<div className='tw:min-w-0 tw:flex-1'>
										{field.type === 'date' && (
											<DatePartsInput
												id={`inline-${row.rowNumber}-${need.field}`}
												parts={partsOf(row.rowNumber, need.field)}
												onChange={(parts) => onFill([row.rowNumber], { parts: { [need.field]: parts } })}
											/>
										)}
										{field.type === 'reference' && (
											<RecordPicker
												id={`inline-${row.rowNumber}-${need.field}`}
												options={optionsByField[need.field]}
												suggestions={need.suggestions}
												selected={
													fill?.ids?.[need.field]
														? { id: fill.ids[need.field], label: fill.labels?.[need.field] || fill.ids[need.field] }
														: null
												}
												onChange={(option) =>
													onFill([row.rowNumber], {
														ids: { [need.field]: option?.id || '' },
														labels: { [need.field]: option?.label || '' },
													})
												}
											/>
										)}
										{field.type !== 'reference' && field.type !== 'date' && (
											<ValueInput
												id={`inline-${row.rowNumber}-${need.field}`}
												field={field}
												value={fill?.values?.[need.field]}
												onChange={(value) => onFill([row.rowNumber], { values: { [need.field]: value } })}
											/>
										)}
										{need.optional && need.reason && <div className='tw:mt-0.5 tw:text-[12px] tw:text-ink-2'>{need.reason}</div>}
									</div>
								</div>
							);
						}
						const filled = fill?.labels?.[need.field];
						return (
							<div key={need.field} className={need.optional ? 'tw:text-ink-2' : 'tw:text-warn'}>
								{fieldLabel(need.field)}: {need.reason}
								{filled && <span className='tw:ml-1 tw:text-ok'>→ {filled}</span>}
							</div>
						);
					})}
					{row.warnings.map((warning) => (
						<div key={warning} className='tw:text-warn'>
							{warning}
						</div>
					))}
					{hasInvisible(row) && (
						<div className='tw:text-ink-2'>
							<span className='tw:mr-1 tw:rounded tw:bg-warn-soft tw:px-1 tw:font-mono tw:text-warn'>⍽</span>
							{t('import.invisibleChars')}
						</div>
					)}
				</div>
			),
		},
	];

	const counts = { all: validation.total, ...validation.counts };
	const busy = !!progress;
	const drawerFieldLabel = fieldLabel;

	return (
		<div className='tw:space-y-4'>
			<div className='tw:flex tw:flex-wrap tw:gap-2'>
				{GROUPS.filter((g) => g === 'all' || counts[g] > 0).map((group) => (
					<button
						key={group}
						type='button'
						onClick={() => {
							setFilter(group);
							setPage(1);
						}}
						className={classnames(
							'tw:flex tw:min-w-[120px] tw:flex-col tw:rounded-panel tw:border tw:px-4 tw:py-2.5 tw:text-left tw:transition-colors',
							filter === group ? 'tw:border-accent tw:bg-accent-soft' : 'tw:border-border tw:bg-surface tw:hover:border-ink-3',
						)}
					>
						<span className='tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3'>
							{t(`import.group.${group}`)}
						</span>
						<span className='tw:font-mono tw:text-[22px] tw:font-semibold tw:text-ink-0'>{counts[group] || 0}</span>
					</button>
				))}
			</div>

			<UnresolvedValues
				groups={unresolvedGroups}
				fieldLabel={fieldLabel}
				optionsByField={optionsByField}
				picks={picks}
				onPick={onPick}
			/>

			<div>
				<h3 className='tw:m-0 tw:text-[15px] tw:font-semibold tw:text-ink-0'>{t('import.rowsTable.title')}</h3>
				<p className='tw:mt-1 tw:mb-0 tw:text-[13px] tw:text-ink-2'>{t('import.rowsTable.help')}</p>
			</div>

			{dateFields.map((field) => (
				<div key={field.key} className='tw:flex tw:flex-wrap tw:items-end tw:gap-3'>
					<FormField id={`month-${field.key}`} label={t('import.monthYear').replace('{field}', fieldLabel(field.key).toLowerCase())}>
						<div className='tw:flex tw:gap-2'>
							<input
								aria-label={t('import.dateParts.day')}
								placeholder={t('import.dateParts.day')}
								inputMode='numeric'
								maxLength={2}
								className={`${controlClass} tw:w-[64px] tw:px-2.5`}
								value={monthYear[field.key]?.day || ''}
								onChange={(e) => applyMonthYear(field.key, { day: e.target.value.replace(/\D/g, '') })}
							/>
							<Select
								id={`month-${field.key}`}
								value={monthYear[field.key]?.month || ''}
								onChange={(e) => applyMonthYear(field.key, { month: e.target.value })}
								placeholder={t('import.dateParts.month')}
								options={MONTH_OPTIONS}
								className='tw:w-[160px]'
							/>
							<input
								aria-label={t('import.dateParts.year')}
								placeholder={t('import.dateParts.year')}
								inputMode='numeric'
								maxLength={4}
								className={`${controlClass} tw:w-[90px] tw:px-2.5`}
								value={monthYear[field.key]?.year || ''}
								onChange={(e) => applyMonthYear(field.key, { year: e.target.value.replace(/\D/g, '') })}
							/>
						</div>
					</FormField>
					<p className='tw:m-0 tw:max-w-[420px] tw:text-[12.5px] tw:text-ink-3'>{t('import.monthYearHelp')}</p>
				</div>
			))}

			<DataTable
				columns={columns}
				rows={pageRows}
				rowKey={(row) => row.rowNumber}
				onRowClick={(row) => row.status === 'existing' && setDrawerRow(row)}
				rowActions={(row) =>
					row.status === 'existing' ? (
						<Button size='sm' variant='ghost' onClick={() => setDrawerRow(row)}>
							Ver cambios
						</Button>
					) : null
				}
				selection={{
					selectedIds: [...included],
					onToggle: toggle,
					onToggleAll: toggleAllVisible,
					isSelectable: (row) => SELECTABLE.has(row.status),
				}}
			/>

			{visibleRows.length > PAGE_SIZE && (
				<Paginator totalCount={visibleRows.length} pageSize={PAGE_SIZE} currentPage={page} onPageChange={setPage} />
			)}

			<div className='tw:sticky tw:bottom-0 tw:flex tw:flex-wrap tw:items-center tw:gap-4 tw:rounded-panel tw:border tw:border-border tw:bg-surface tw:px-4 tw:py-3 tw:shadow-sm'>
				<span className='tw:text-[13.5px] tw:text-ink-0'>
					<strong>{plan.create.length}</strong> {t('import.toCreate')}
					{schema.matchOptions && (
						<>
							, <strong>{plan.updates.length}</strong> {t('import.toUpdate')}
						</>
					)}
				</span>
				{dirty && !busy && (
					<span className='tw:flex tw:items-center tw:gap-2 tw:text-[13px] tw:text-warn'>
						{t('import.pendingFills')}
						<Button size='sm' onClick={onRevalidate} disabled={validating}>
							{validating ? <Spinner as='span' size='sm' animation='border' /> : t('import.reviewAgain')}
						</Button>
					</span>
				)}
				{busy && (
					<div className='tw:flex tw:min-w-[200px] tw:flex-1 tw:items-center tw:gap-2'>
						<ProgressBar value={progress.done} max={progress.total} />
						<span className='tw:font-mono tw:text-[12px] tw:text-ink-2'>
							{progress.done}/{progress.total}
						</span>
					</div>
				)}
				<Button
					variant='primary'
					className='tw:ml-auto'
					disabled={busy || dirty || plan.create.length + plan.updates.length === 0}
					onClick={() => onApply(plan)}
				>
					{busy ? (
						<>
							<Spinner as='span' size='sm' animation='border' className='tw:mr-1' />
							{t('import.importing')}
						</>
					) : (
						t('import.apply')
					)}
				</Button>
			</div>

			<RecordDrawer
				show={!!drawerRow}
				onClose={() => setDrawerRow(null)}
				meta={drawerRow ? `${t('import.row')} ${drawerRow.rowNumber}` : null}
				title={drawerRow?.preview?.account || ''}
				footer={
					drawerRow && (
						<>
							<Button size='sm' onClick={() => setAllModes(drawerRow, 'keep')}>
								{t('import.allKeep')}
							</Button>
							<Button size='sm' onClick={() => setAllModes(drawerRow, 'file')}>
								{t('import.allUseFile')}
							</Button>
						</>
					)
				}
			>
				{drawerRow?.diffs?.map((diff) => {
					const mode = modeOf(drawerRow, diff.field);
					const field = fieldsByKey.get(diff.field);
					return (
						<div key={diff.field} className='tw:space-y-2 tw:border-b tw:border-border tw:pb-4'>
							<div className='tw:flex tw:items-center tw:justify-between tw:gap-2'>
								<strong className='tw:text-[13.5px]'>{drawerFieldLabel(diff.field)}</strong>
								<div className='tw:inline-flex tw:overflow-hidden tw:rounded-[7px] tw:border tw:border-border-strong'>
									{['keep', 'file'].map((option) => (
										<button
											key={option}
											type='button'
											onClick={() => setMode(drawerRow, diff.field, option)}
											className={classnames(
												'tw:border-0 tw:px-3 tw:py-1 tw:text-[12.5px]',
												mode === option ? 'tw:bg-accent tw:text-white' : 'tw:bg-surface tw:text-ink-1',
											)}
										>
											{option === 'keep' ? t('import.keepCurrent') : t('import.useFile')}
										</button>
									))}
								</div>
							</div>
							<div className='tw:text-[12.5px] tw:text-ink-2'>
								{t('import.currentValue')}: <span className='tw:text-ink-0'>{asText(diff.current) || '—'}</span>
							</div>
							{diff.field === 'email' && mode === 'file' && (
								<div className='tw:text-[12px] tw:text-warn'>El email también se actualiza en el contacto principal.</div>
							)}
							{field?.type === 'enum' ? (
								<Select
									id={`final-${diff.field}`}
									value={finalOf(drawerRow, diff)}
									disabled={mode !== 'file'}
									onChange={(e) => setFinal(drawerRow, diff.field, e.target.value)}
									options={Object.keys(field.enum)}
								/>
							) : (
								<input
									aria-label={`${t('import.finalValue')} ${drawerFieldLabel(diff.field)}`}
									className={`${controlClass} tw:w-full tw:px-2.5`}
									value={finalOf(drawerRow, diff)}
									disabled={mode !== 'file'}
									onChange={(e) => setFinal(drawerRow, diff.field, e.target.value)}
								/>
							)}
						</div>
					);
				})}
			</RecordDrawer>
		</div>
	);
}

StepReview.propTypes = {
	schema: PropTypes.object.isRequired,
	validation: PropTypes.object.isRequired,
	table: PropTypes.object.isRequired,
	mapping: PropTypes.object.isRequired,
	progress: PropTypes.object,
	onApply: PropTypes.func.isRequired,
	fills: PropTypes.object,
	onFill: PropTypes.func.isRequired,
	picks: PropTypes.object,
	onPick: PropTypes.func.isRequired,
	dirty: PropTypes.bool,
	validating: PropTypes.bool,
	onRevalidate: PropTypes.func.isRequired,
};
